import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, MessageSquare, Plus, Pencil, Trash2, LogOut,
  Cpu, Sparkles, TrendingUp, Zap, ExternalLink, Home, Bell, Rocket
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-studio-hub-33/artifacts/l67b1y1e_Color%20logo%20-%20no%20background.png";

const AI_TYPES = [
  { value: "agentic", label: "Agentic AI", icon: Cpu },
  { value: "generative", label: "Generative AI", icon: Sparkles },
  { value: "predictive", label: "Predictive AI", icon: TrendingUp },
  { value: "nextgen", label: "Next-Gen AI", icon: Zap },
];

const STATUSES = [
  { value: "live", label: "Live" },
  { value: "beta", label: "Beta" },
  { value: "coming_soon", label: "Coming Soon" },
];

const ICONS = [
  "cpu", "sparkles", "trending-up", "zap", "brain", "bot",
  "chart", "database", "file", "globe", "layers", "lightbulb",
  "rocket", "server", "shield", "target", "terminal", "workflow"
];

const getAuthHeaders = () => {
  const auth = localStorage.getItem("adminAuth");
  if (!auth) return {};
  return { Authorization: `Basic ${auth}` };
};

const ProductForm = ({ product, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    features: product?.features?.join("\n") || "",
    status: product?.status || "coming_soon",
    demo_link: product?.demo_link || "",
    icon: product?.icon || "cpu",
    ai_type: product?.ai_type || "agentic",
    order: product?.order || 0,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description) {
      toast.error("Name and description are required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        features: formData.features.split("\n").filter(f => f.trim()),
        order: parseInt(formData.order) || 0,
      };

      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Product created");
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="product-form">
      <div>
        <label className="form-label">Product Name</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-base w-full"
          placeholder="e.g., AI Trading Agent"
          data-testid="product-name-input"
        />
      </div>

      <div>
        <label className="form-label">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-base w-full min-h-[100px]"
          placeholder="Brief description of the product..."
          data-testid="product-description-input"
        />
      </div>

      <div>
        <label className="form-label">Features (one per line)</label>
        <Textarea
          value={formData.features}
          onChange={(e) => setFormData({ ...formData, features: e.target.value })}
          className="input-base w-full min-h-[100px]"
          placeholder="Real-time market analysis&#10;Automated trading signals&#10;Risk management"
          data-testid="product-features-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">AI Type</label>
          <Select
            value={formData.ai_type}
            onValueChange={(value) => setFormData({ ...formData, ai_type: value })}
          >
            <SelectTrigger className="input-base w-full" data-testid="product-ai-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AI_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="form-label">Status</label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger className="input-base w-full" data-testid="product-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">Demo Link (optional)</label>
          <Input
            value={formData.demo_link}
            onChange={(e) => setFormData({ ...formData, demo_link: e.target.value })}
            className="input-base w-full"
            placeholder="https://..."
            data-testid="product-demo-link-input"
          />
        </div>

        <div>
          <label className="form-label">Display Order</label>
          <Input
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: e.target.value })}
            className="input-base w-full"
            placeholder="0"
            data-testid="product-order-input"
          />
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button
          type="button"
          onClick={onClose}
          className="btn-outline flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="btn-primary flex-1"
          data-testid="product-save-button"
        >
          {loading ? "Saving..." : product ? "Update Product" : "Create Product"}
        </Button>
      </div>
    </form>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/admin/products/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingProduct(null);
    setDialogOpen(true);
  };

  const aiTypeLabel = (type) => {
    const config = AI_TYPES.find(t => t.value === type);
    return config?.label || type;
  };

  const statusLabel = (status) => {
    const config = STATUSES.find(s => s.value === status);
    return config?.label || status;
  };

  return (
    <div data-testid="products-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-xl uppercase">Products</h2>
        <Button onClick={openNewDialog} className="btn-primary flex items-center gap-2" data-testid="add-product-button">
          <Plus size={16} /> Add Product
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 card-base">
          <Package className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-500 mb-4">No products yet</p>
          <Button onClick={openNewDialog} className="btn-primary">
            Create Your First Product
          </Button>
        </div>
      ) : (
        <div className="card-base overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#222]">
                <TableHead className="font-mono text-xs uppercase text-gray-500">Name</TableHead>
                <TableHead className="font-mono text-xs uppercase text-gray-500">AI Type</TableHead>
                <TableHead className="font-mono text-xs uppercase text-gray-500">Status</TableHead>
                <TableHead className="font-mono text-xs uppercase text-gray-500">Order</TableHead>
                <TableHead className="font-mono text-xs uppercase text-gray-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} className="border-b border-[#222] hover:bg-[#111]" data-testid={`product-row-${product.id}`}>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-[#F87931]">
                      {aiTypeLabel(product.ai_type)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`font-mono text-xs px-2 py-1 ${
                      product.status === 'live' ? 'status-live' :
                      product.status === 'beta' ? 'status-beta' : 'status-coming_soon'
                    }`}>
                      {statusLabel(product.status)}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-gray-500">{product.order}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {product.demo_link && (
                        <a
                          href={product.demo_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-[#F87931]"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                      <button
                        onClick={() => openEditDialog(product)}
                        className="p-2 text-gray-500 hover:text-white"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-gray-500 hover:text-red-500"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-[#222] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl uppercase">
              {editingProduct ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>
          <ProductForm
            product={editingProduct}
            onSave={fetchProducts}
            onClose={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: getAuthHeaders(),
      });
      setContacts(response.data);
    } catch (error) {
      toast.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Contact deleted");
      fetchContacts();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div data-testid="contacts-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-xl uppercase">Contact Submissions</h2>
        <span className="font-mono text-sm text-gray-500">{contacts.length} total</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 card-base">
          <MessageSquare className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-500">No contact submissions yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div key={contact.id} className="card-base p-6" data-testid={`contact-${contact.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-medium text-lg">{contact.name}</h3>
                  <a href={`mailto:${contact.email}`} className="text-[#F87931] text-sm hover:underline">
                    {contact.email}
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-xs text-gray-500">
                    {formatDate(contact.created_at)}
                  </span>
                  <button
                    onClick={() => handleDelete(contact.id)}
                    className="p-2 text-gray-500 hover:text-red-500"
                    data-testid={`delete-contact-${contact.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="text-gray-400 whitespace-pre-wrap">{contact.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SubscribersTab = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/subscribers`, {
        headers: getAuthHeaders(),
      });
      setSubscribers(response.data);
    } catch (error) {
      toast.error("Failed to fetch subscribers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this subscriber?")) return;
    
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Subscriber deleted");
      fetchSubscribers();
    } catch (error) {
      toast.error("Failed to delete subscriber");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportEmails = () => {
    const emails = subscribers.map(s => s.email).join("\n");
    const blob = new Blob([emails], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inopsia-subscribers.txt";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Emails exported!");
  };

  return (
    <div data-testid="subscribers-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-xl uppercase">Subscribers</h2>
        <div className="flex items-center gap-4">
          <span className="font-mono text-sm text-gray-500">{subscribers.length} total</span>
          {subscribers.length > 0 && (
            <Button onClick={exportEmails} className="btn-outline text-sm" data-testid="export-emails">
              Export Emails
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 card-base">
          <Bell className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-500">No subscribers yet</p>
        </div>
      ) : (
        <div className="card-base overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#222]">
                <TableHead className="font-mono text-xs uppercase text-gray-500">Email</TableHead>
                <TableHead className="font-mono text-xs uppercase text-gray-500">Subscribed</TableHead>
                <TableHead className="font-mono text-xs uppercase text-gray-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscribers.map((subscriber) => (
                <TableRow key={subscriber.id} className="border-b border-[#222] hover:bg-[#111]" data-testid={`subscriber-${subscriber.id}`}>
                  <TableCell>
                    <a href={`mailto:${subscriber.email}`} className="text-[#F87931] hover:underline">
                      {subscriber.email}
                    </a>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-gray-500">
                    {formatDate(subscriber.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => handleDelete(subscriber.id)}
                      className="p-2 text-gray-500 hover:text-red-500"
                      data-testid={`delete-subscriber-${subscriber.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const VenturesManagementTab = () => {
  const [ventures, setVentures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVenture, setEditingVenture] = useState(null);

  const fetchVentures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/ventures`, {
        headers: getAuthHeaders(),
      });
      setVentures(response.data);
    } catch (error) {
      toast.error("Failed to fetch ventures");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVentures();
  }, [fetchVentures]);

  const openEditDialog = (venture) => {
    setEditingVenture(venture);
    setDialogOpen(true);
  };

  const getStatusLabel = (venture) => {
    const launchDate = new Date(venture.launch_date);
    const now = new Date();
    if (now > launchDate) {
      return { label: "LAUNCHED", color: "text-emerald-500", bg: "bg-emerald-500/20" };
    }
    return { label: "UPCOMING", color: "text-[#F87931]", bg: "bg-[#F87931]/20" };
  };

  return (
    <div data-testid="ventures-management-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-xl uppercase">Ventures</h2>
        <span className="font-mono text-sm text-gray-500">{ventures.length} ventures</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ventures.map((venture) => {
            const status = getStatusLabel(venture);
            return (
              <div 
                key={venture.id}
                className="card-base p-6 border-2 border-[#333]"
                data-testid={`venture-manage-${venture.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-3xl text-[#F87931]" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900 }}>
                      {venture.id}
                    </p>
                    {venture.category && (
                      <p className="text-sm text-gray-400 mt-1">{venture.category}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 ${status.bg} ${status.color}`} style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>
                    {status.label}
                  </span>
                </div>

                <p className="text-sm text-gray-500 mb-2">
                  {venture.quarter} {venture.year}
                </p>

                {venture.name ? (
                  <p className="text-white font-semibold mb-1">{venture.name}</p>
                ) : (
                  <p className="text-gray-600 italic mb-1">No name set</p>
                )}

                {venture.description ? (
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{venture.description}</p>
                ) : (
                  <p className="text-gray-600 italic text-sm mb-3">No description</p>
                )}

                {venture.link && (
                  <a 
                    href={venture.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#F87931] text-sm flex items-center gap-1 hover:underline mb-3"
                  >
                    <ExternalLink size={12} /> {venture.link}
                  </a>
                )}

                <Button
                  onClick={() => openEditDialog(venture)}
                  className="btn-outline w-full text-sm"
                  data-testid={`edit-venture-${venture.id}`}
                >
                  <Pencil size={14} className="mr-2" /> Edit Details
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-[#222] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl uppercase">
              Edit {editingVenture?.id} - {editingVenture?.category || "Venture"}
            </DialogTitle>
          </DialogHeader>
          {editingVenture && (
            <VentureEditForm
              venture={editingVenture}
              onSave={fetchVentures}
              onClose={() => setDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const VentureEditForm = ({ venture, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    category: venture.category || "",
    name: venture.name || "",
    description: venture.description || "",
    link: venture.link || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/admin/ventures/${venture.id}`, formData, {
        headers: getAuthHeaders(),
      });
      toast.success(`${venture.id} updated successfully`);
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to update venture");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="venture-edit-form">
      <div className="p-3 bg-[#111] border border-[#333] mb-4">
        <p className="text-sm text-gray-500">
          <strong className="text-white">{venture.quarter} {venture.year}</strong> — Launch: {new Date(venture.launch_date).toLocaleDateString()}
        </p>
      </div>

      <div>
        <label className="form-label">Category</label>
        <Input
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value.toUpperCase() })}
          className="input-base w-full"
          placeholder="e.g., TRADE TECH"
          data-testid="venture-category-input"
        />
      </div>

      <div>
        <label className="form-label">Product Name (when launched)</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-base w-full"
          placeholder="e.g., TradeTech AI"
          data-testid="venture-name-input"
        />
      </div>

      <div>
        <label className="form-label">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-base w-full min-h-[80px]"
          placeholder="Brief description of the product..."
          data-testid="venture-description-input"
        />
      </div>

      <div>
        <label className="form-label">Link (when live)</label>
        <Input
          value={formData.link}
          onChange={(e) => setFormData({ ...formData, link: e.target.value })}
          className="input-base w-full"
          placeholder="https://..."
          data-testid="venture-link-input"
        />
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="button" onClick={onClose} className="btn-outline flex-1">
          Cancel
        </Button>
        <Button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="venture-save-button">
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </form>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("ventures");

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-[#222] sticky top-0 z-50">
        <div className="container-main">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <img src={LOGO_URL} alt="Inopsia" className="h-6" />
              <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" className="p-2 text-gray-500 hover:text-white" title="View Website" data-testid="view-website-link">
                <Home size={18} />
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-gray-500 hover:text-red-500 font-mono text-sm"
                data-testid="logout-button"
              >
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-[#0A0A0A] border-b border-[#222]">
        <div className="container-main">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab("ventures")}
              className={`py-4 font-mono text-sm uppercase tracking-wider border-b-2 ${
                activeTab === "ventures"
                  ? "text-[#F87931] border-[#F87931]"
                  : "text-gray-500 border-transparent hover:text-white"
              }`}
              data-testid="ventures-tab-button"
            >
              <Rocket size={16} className="inline mr-2" />
              Ventures
            </button>
            <button
              onClick={() => setActiveTab("subscribers")}
              className={`py-4 font-mono text-sm uppercase tracking-wider border-b-2 ${
                activeTab === "subscribers"
                  ? "text-[#F87931] border-[#F87931]"
                  : "text-gray-500 border-transparent hover:text-white"
              }`}
              data-testid="subscribers-tab-button"
            >
              <Bell size={16} className="inline mr-2" />
              Subscribers
            </button>
            <button
              onClick={() => setActiveTab("contacts")}
              className={`py-4 font-mono text-sm uppercase tracking-wider border-b-2 ${
                activeTab === "contacts"
                  ? "text-[#F87931] border-[#F87931]"
                  : "text-gray-500 border-transparent hover:text-white"
              }`}
              data-testid="contacts-tab-button"
            >
              <MessageSquare size={16} className="inline mr-2" />
              Contacts
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="container-main py-8">
        {activeTab === "ventures" && <VenturesManagementTab />}
        {activeTab === "subscribers" && <SubscribersTab />}
        {activeTab === "contacts" && <ContactsTab />}
      </main>
    </div>
  );
}
